import { Mark, mergeAttributes } from '@tiptap/core';

// ═══════════════════════════════════════════════════════════════
// Marky sledování změn (Fáze 3) — vložený a smazaný text.
//
// Mapování na Word (OOXML):
//   insertion → <w:ins w:id=".." w:author=".." w:date="..">
//   deletion  → <w:del w:id=".." w:author=".." w:date="..">  (+ <w:delText>)
//
// KLÍČOVÉ: smazaný text se z dokumentu NEODSTRANÍ — zůstane v něm označený
// markem `deletion` a jen se vizuálně přeškrtne. Teprve „Přijmout změnu"
// ho odstraní doopravdy. Bez toho by nešlo změnu odmítnout ani exportovat
// do Wordu tak, aby ji viděl ve svém review panelu.
//
// V HTML serializaci:
//   <span data-track-ins data-author=".." data-date="..">…</span>
//   <span data-track-del data-author=".." data-date="..">…</span>
// Díky tomu vazba přežije getHTML() → export i round-trip.
// ═══════════════════════════════════════════════════════════════

export interface TrackAttrs {
  author: string | null;
  date: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    trackChanges: {
      /** Označí rozsah jako vložený (autor + čas). */
      setInsertion: (attrs: TrackAttrs) => ReturnType;
      /** Označí rozsah jako smazaný (autor + čas). */
      setDeletion: (attrs: TrackAttrs) => ReturnType;
      /** Sundá značku vložení z rozsahu. */
      unsetInsertion: () => ReturnType;
      /** Sundá značku smazání z rozsahu. */
      unsetDeletion: () => ReturnType;
    };
  }
}

/** Společné atributy obou marků — autor a časové razítko změny. */
function trackAttributes(prefix: 'ins' | 'del') {
  return {
    author: {
      default: null,
      parseHTML: (el: HTMLElement) => el.getAttribute('data-author'),
      renderHTML: (attrs: any) => (attrs.author ? { 'data-author': attrs.author } : {}),
    },
    date: {
      default: null,
      parseHTML: (el: HTMLElement) => el.getAttribute('data-date'),
      renderHTML: (attrs: any) => (attrs.date ? { 'data-date': attrs.date } : {}),
    },
    // Marker pro parseHTML — odlišuje ins od del.
    kind: {
      default: prefix,
      rendered: false,
    },
  };
}

export const Insertion = Mark.create({
  name: 'insertion',
  // Vložení se může kombinovat s formátováním i s komentáři.
  excludes: '',
  inclusive: false,

  addAttributes() {
    return trackAttributes('ins');
  },

  parseHTML() {
    return [{ tag: 'span[data-track-ins]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-track-ins': '',
        class: 'track-ins',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setInsertion:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
      unsetInsertion:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

export const Deletion = Mark.create({
  name: 'deletion',
  excludes: '',
  inclusive: false,

  addAttributes() {
    return trackAttributes('del');
  },

  parseHTML() {
    return [{ tag: 'span[data-track-del]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-track-del': '',
        class: 'track-del',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setDeletion:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
      unsetDeletion:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});
